// src/pages/alianzas/PublicCloudQBRPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Award, ChevronDown, Filter, Globe2, GraduationCap, Layers, RotateCcw, Settings2, Sparkles, Store,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { certificacionesApi, apiErrorMessage } from '../../services/certificacionesApi';
import { alianzasApi } from '../../services/alianzasApi';
import { useAuth } from '../../contexts/AuthContext';
import { ACCENT, BORDER, CARD, PAGE_BG, colorForFabricante, fmtFecha, tint } from '../../components/alianzas/ui';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const TABS = [
  { id: 'certificaciones', label: 'Certificaciones', icon: Award },
  { id: 'capacitacion', label: 'Capacitación', icon: GraduationCap },
  { id: 'marketing', label: 'Marketing y Eventos', icon: Sparkles },
  { id: 'marketplace', label: 'Marketplace y Soluciones', icon: Store },
  { id: 'especializaciones', label: 'Especializaciones', icon: Layers },
];

// ─── helpers de periodo (trimestre / mes / año) ──────────────────────────────

const quarterOf = (mes) => Math.floor((mes - 1) / 3) + 1;

function partsOf(fecha) {
  if (!fecha) return null;
  const d = new Date(`${String(fecha).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const mes = d.getMonth() + 1;
  return { anio: d.getFullYear(), mes, trimestre: quarterOf(mes) };
}

function countBy(rows, key) {
  const map = new Map();
  rows.forEach(r => { const k = r[key] || 'N/A'; map.set(k, (map.get(k) || 0) + 1); });
  return [...map.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

function usePeriodo(anioDefault) {
  const [modo, setModo] = useState('trimestre'); // trimestre | mes | anio
  const [anio, setAnio] = useState(anioDefault);
  const [valores, setValores] = useState([quarterOf(new Date().getMonth() + 1)]);

  const toggle = (v) => setValores(vs => (vs.includes(v) ? vs.filter(x => x !== v) : [...vs, v].sort((a, b) => a - b)));

  // valores vacío = "todos" dentro del modo/año activo
  const matches = useCallback((fecha) => {
    const p = partsOf(fecha);
    if (!p) return false;
    if (modo === 'anio') return valores.length ? valores.includes(p.anio) : true;
    if (p.anio !== anio) return false;
    if (!valores.length) return true;
    return modo === 'trimestre' ? valores.includes(p.trimestre) : valores.includes(p.mes);
  }, [modo, anio, valores]);

  const label = useMemo(() => {
    if (modo === 'anio') return valores.length ? valores.join(' / ') : 'Todos los años';
    const base = modo === 'trimestre'
      ? (valores.length ? valores.map(v => `Q${v}`).join('/') : 'Todos')
      : (valores.length ? valores.map(v => MESES[v - 1]).join('/') : 'Todos');
    return `${base} ${anio}`;
  }, [modo, anio, valores]);

  return { modo, setModo, anio, setAnio, valores, setValores, toggle, matches, label };
}

function PeriodoFiltro({ periodo, anios }) {
  const [open, setOpen] = useState(false);
  const { modo, setModo, anio, setAnio, valores, toggle, setValores, label } = periodo;

  const opcionesValor = modo === 'trimestre' ? [1, 2, 3, 4]
    : modo === 'mes' ? MESES.map((_, i) => i + 1)
    : anios;

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors"
        style={{ borderColor: BORDER, color: ACCENT, background: tint(0.1) }}
      >
        <Filter size={13} /> {label} <ChevronDown size={13} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`${CARD} absolute right-0 mt-2 z-50 p-4 w-72`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wide text-white/40">Periodo</span>
              <button onClick={() => setValores([])} className="text-[11px] text-white/40 hover:text-white flex items-center gap-1">
                <RotateCcw size={11} /> Todos
              </button>
            </div>

            <div className="inline-flex rounded-lg border border-sky-400/20 bg-[#061a2b] p-1 mb-3">
              {[{ id: 'trimestre', label: 'Trimestre' }, { id: 'mes', label: 'Mes' }, { id: 'anio', label: 'Año' }].map(m => (
                <button key={m.id}
                  onClick={() => { setModo(m.id); setValores([]); }}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all"
                  style={{ background: modo === m.id ? tint(.2) : 'transparent', color: modo === m.id ? ACCENT : 'rgba(255,255,255,.5)' }}>
                  {m.label}
                </button>
              ))}
            </div>

            {modo !== 'anio' && (
              <select
                className="w-full mb-3 rounded-lg px-3 py-2 text-sm bg-[#071f30] border border-sky-400/20 text-white"
                value={anio}
                onChange={e => setAnio(Number(e.target.value))}
              >
                {anios.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            )}

            <div className="flex flex-wrap gap-1.5">
              {opcionesValor.map(v => {
                const sel = valores.includes(v);
                const texto = modo === 'trimestre' ? `Q${v}` : modo === 'mes' ? MESES[v - 1] : v;
                return (
                  <button key={v} onClick={() => toggle(v)}
                    className="px-2.5 py-1 rounded-md text-xs font-semibold transition-all"
                    style={{ background: sel ? tint(.25) : 'rgba(255,255,255,.05)', color: sel ? ACCENT : 'rgba(255,255,255,.55)', border: `1px solid ${sel ? BORDER : 'rgba(255,255,255,.08)'}` }}>
                    {texto}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── piezas visuales ──────────────────────────────────────────────────────────

function Kpi({ label, valor, tono, nota }) {
  return (
    <div className={`${CARD} px-4 py-3`}>
      <p className="text-[11px] uppercase tracking-wide text-white/40">{label}</p>
      <p className="text-2xl font-bold mt-0.5" style={{ color: tono || '#fff' }}>{valor}</p>
      {nota && <p className="text-[10px] text-white/30 mt-0.5">{nota}</p>}
    </div>
  );
}

function VisualCard({ title, className = '', children }) {
  return (
    <div className={`${CARD} p-4 ${className}`}>
      <h3 className="text-sm font-semibold text-white/85 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function MiniTable({ columns, rows, empty = 'Sin datos' }) {
  if (!rows.length) {
    return <div className="h-[100px] flex items-center justify-center text-xs text-white/30 text-center px-4">{empty}</div>;
  }
  return (
    <div className="overflow-auto max-h-[320px]">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-[#082234] z-10">
          <tr>{columns.map(c => <th key={c.key} className="text-left py-2 px-2 font-medium text-white/40 uppercase text-[9px]">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/5">
              {columns.map(c => <td key={c.key} className="py-2 px-2 text-white/65">{c.render ? c.render(row) : (row[c.key] ?? '—')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Donut({ data }) {
  const [hover, setHover] = useState(null);
  if (!data.length) return <div className="h-[220px] flex items-center justify-center text-xs text-white/30">Sin datos</div>;
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  return (
    <div className="flex flex-wrap items-center gap-6">
      <div style={{ width: 200, height: 200 }} className="shrink-0">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}
              onMouseEnter={(_, i) => setHover(i)} onMouseLeave={() => setHover(null)}>
              {data.map((entry, i) => (
                <Cell key={entry.name} fill={colorForFabricante(entry.name, i)}
                  opacity={hover === null || hover === i ? 1 : .3}
                  stroke="#04121f" strokeWidth={2} cursor="pointer" />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const item = payload[0];
                return (
                  <div className="rounded-lg px-3 py-2 text-xs shadow-xl border" style={{ borderColor: BORDER, background: '#0b2b44', color: '#fff' }}>
                    <div className="font-semibold" style={{ color: item.payload.fill }}>{item.name}</div>
                    <div className="text-white/70">{item.value} · {((item.value / total) * 100).toFixed(1)}%</div>
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="space-y-1.5 text-xs min-w-[150px]">
        {data.map((d, i) => (
          <li key={d.name}
            className="flex items-center gap-2 cursor-pointer transition-opacity"
            style={{ opacity: hover === null || hover === i ? 1 : .4 }}
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorForFabricante(d.name, i) }} />
            <span className="text-white/70">{d.name}</span>
            <span className="text-white/40 ml-auto font-semibold">{((d.value / total) * 100).toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── pagina ──────────────────────────────────────────────────────────────────

export default function PublicCloudQBRPage() {
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState('certificaciones');
  const [examenes, setExamenes] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [qbrData, setQbrData] = useState({ eventos: [], marketplaces: [], especializaciones: [] });
  const [qbrLoading, setQbrLoading] = useState(true);
  const [qbrError, setQbrError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      certificacionesApi.listExamenes({ limit: 1000 }),
      certificacionesApi.listCursos({ limit: 1000 }),
    ]).then(([e, c]) => { setExamenes(e.data || []); setCursos(c.data || []); })
      .catch(err => setError(apiErrorMessage(err, 'Error cargando información')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      alianzasApi.listQbr('eventos', { limit: 1000 }),
      alianzasApi.listQbr('marketplaces', { limit: 1000 }),
      alianzasApi.listQbr('especializaciones', { limit: 1000 }),
    ]).then(([eventos, marketplaces, especializaciones]) => {
      if (!mounted) return;
      setQbrData({
        eventos: eventos.data || [],
        marketplaces: marketplaces.data || [],
        especializaciones: especializaciones.data || [],
      });
    }).catch(err => {
      if (mounted) setQbrError(apiErrorMessage(err, 'Error cargando información QBR'));
    }).finally(() => {
      if (mounted) setQbrLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  const anioActual = new Date().getFullYear();
  const anios = useMemo(() => {
    const set = new Set([anioActual]);
    examenes.forEach(e => { const p = partsOf(e.fecha_examen); if (p) set.add(p.anio); });
    cursos.forEach(c => { const p = partsOf(c.fecha_inicio); if (p) set.add(p.anio); });
    return [...set].sort((a, b) => b - a);
  }, [examenes, cursos, anioActual]);

  const periodo = usePeriodo(anioActual);
  const matchesPeriodo = periodo.matches;

  const aprobadosPeriodo = useMemo(
    () => examenes.filter(e => e.resultado === 'Aprobado' && matchesPeriodo(e.fecha_examen)),
    [examenes, matchesPeriodo]
  );

  const totalNubePublica = useMemo(() => examenes.filter(e => e.activo_efectivo).length, [examenes]);

  const fabricantesDonut = useMemo(() => countBy(aprobadosPeriodo, 'fabricante'), [aprobadosPeriodo]);

  const desglose = useMemo(() => {
    const map = new Map();
    aprobadosPeriodo.forEach(e => {
      const key = `${e.fabricante || 'N/A'}|${e.especialidad || 'N/A'}`;
      const item = map.get(key) || {
        fabricante: e.fabricante || 'N/A', categoria: e.especialidad || 'N/A',
        fundamentals: 0, associate: 0, proExpert: 0, total: 0,
      };
      if (e.nivel === 'Fundamentals') item.fundamentals += 1;
      else if (e.nivel === 'Associate') item.associate += 1;
      else item.proExpert += 1;
      item.total += 1;
      map.set(key, item);
    });
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [aprobadosPeriodo]);

  const cursosPeriodo = useMemo(
    () => cursos.filter(c => c.estatus === 'En Proceso' && matchesPeriodo(c.fecha_inicio)),
    [cursos, matchesPeriodo]
  );

  const capacitaciones = useMemo(() => {
    const map = new Map();
    cursosPeriodo.forEach(c => {
      const key = `${c.fabricante || 'N/A'}|${c.programa || 'N/A'}`;
      const item = map.get(key) || { fabricante: c.fabricante || 'N/A', programa: c.programa || 'N/A', participantes: 0 };
      item.participantes += 1;
      map.set(key, item);
    });
    return [...map.values()].sort((a, b) => b.participantes - a.participantes);
  }, [cursosPeriodo]);

  const eventosPeriodo = qbrData.eventos.filter(evento => !evento.fecha || periodo.matches(evento.fecha));
  const marketplacesPeriodo = qbrData.marketplaces.filter(registro => {
    const fechas = [registro.fecha_publicacion, registro.fecha_publicacion_ofertas_proximas].filter(Boolean);
    return !fechas.length || fechas.some(periodo.matches);
  });
  const especializacionesPeriodo = qbrData.especializaciones.filter(registro =>
    !registro.fecha_obtencion || periodo.matches(registro.fecha_obtencion)
  );

  const qbrTable = (rows, columns, empty) => {
    if (qbrLoading) return <div className="h-[100px] flex items-center justify-center text-xs text-white/40">Cargando datos QBR…</div>;
    if (qbrError) return <div className="p-4 text-sm text-red-300">{qbrError}</div>;
    return <MiniTable rows={rows} columns={columns} empty={empty} />;
  };

  return (
    <div className="p-6 space-y-6 min-h-screen" style={{ background: PAGE_BG, backgroundAttachment: 'fixed' }}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Globe2 size={22} style={{ color: ACCENT }} />
            Public Cloud & Alliances QBR
          </h1>
          <p className="text-sm text-white/50 mt-1">Dashboard de Gestión de Fabricantes</p>
        </div>
        <div className="flex items-center gap-2">
          {hasPermission('alianzas.qbr.gestion.view') && (
            <Link to="/alianzas/qbr-gestion" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-sky-400/20 text-sky-300 hover:bg-sky-400/10">
              <Settings2 size={14} /> Gestionar datos
            </Link>
          )}
          <PeriodoFiltro periodo={periodo} anios={anios} />
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        {TABS.map(t => {
          const Icono = t.icon;
          const activa = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={{
                background: activa ? tint(0.18) : 'rgba(255,255,255,0.04)',
                border: `1px solid ${activa ? BORDER : 'rgba(255,255,255,0.08)'}`,
                color: activa ? ACCENT : 'rgba(255,255,255,0.6)',
              }}>
              <Icono size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {error && <div className={`${CARD} p-4 text-sm text-red-300`}>{error}</div>}

      {loading ? (
        <div className={`${CARD} p-10 flex items-center justify-center text-white/40 text-sm`}>Cargando…</div>
      ) : (
        <>
          {tab === 'certificaciones' && (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
              <div className="xl:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Kpi label="Total Nube Pública (aprox)" valor={totalNubePublica} tono={ACCENT} />
                <Kpi label={`Logradas en ${periodo.label}`} valor={aprobadosPeriodo.length} tono="#4ade80" />
              </div>

              <VisualCard title={`Total Certificaciones Públicas por Fabricante (${periodo.label})`} className="xl:col-span-12">
                <Donut data={fabricantesDonut} />
              </VisualCard>

              <VisualCard title={`Desglose de Logros en ${periodo.label}`} className="xl:col-span-12">
                <MiniTable
                  rows={desglose}
                  empty="No hay certificaciones aprobadas en el periodo seleccionado."
                  columns={[
                    { key: 'fabricante', label: 'Fabricante' },
                    { key: 'categoria', label: 'Categoría' },
                    { key: 'fundamentals', label: 'Fundamentals' },
                    { key: 'associate', label: 'Associate' },
                    { key: 'proExpert', label: 'Pro/Expert' },
                    { key: 'total', label: `Total ${periodo.label}` },
                  ]}
                />
              </VisualCard>
            </div>
          )}

          {tab === 'capacitacion' && (
            <VisualCard title={`Capacitaciones en Curso (${periodo.label})`}>
              <MiniTable
                rows={capacitaciones}
                empty="No hay capacitaciones en curso dentro del periodo seleccionado."
                columns={[
                  { key: 'fabricante', label: 'Fabricante' },
                  { key: 'programa', label: 'Capacitación' },
                  { key: 'participantes', label: 'Participantes' },
                ]}
              />
            </VisualCard>
          )}

          {tab === 'marketing' && (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
              <div className="xl:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Kpi label={`Eventos en ${periodo.label}`} valor={eventosPeriodo.length} tono={ACCENT} />
                <Kpi label="Asistentes registrados" valor={eventosPeriodo.reduce((total, row) => total + (Number(row.numero_asistentes) || 0), 0)} tono="#4ade80" />
              </div>
              <VisualCard title={`Marketing y Eventos (${periodo.label})`} className="xl:col-span-12">
                {qbrTable(eventosPeriodo, [
                  { key: 'fabricante', label: 'Fabricante' },
                  { key: 'nombre_evento', label: 'Evento' },
                  { key: 'topic_foco', label: 'Topic / Foco' },
                  { key: 'fecha', label: 'Fecha', render: row => fmtFecha(row.fecha) },
                  { key: 'estatus', label: 'Estatus' },
                  { key: 'numero_asistentes', label: 'Asistentes' },
                  { key: 'fondos_inversion', label: 'Fondos', render: row => row.fondos_inversion == null ? '—' : `${Number(row.fondos_inversion).toLocaleString('es-MX')} ${row.moneda || 'MXN'}` },
                  { key: 'reclamado', label: 'Reclamado', render: row => row.reclamado == null ? '—' : (row.reclamado ? 'Sí' : 'No') },
                ], 'No hay eventos en el periodo seleccionado.')}
              </VisualCard>
            </div>
          )}
          {tab === 'marketplace' && (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
              <div className="xl:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Kpi label="Fabricantes con ofertas" valor={marketplacesPeriodo.filter(row => row.ofertas_actuales).length} tono={ACCENT} />
                <Kpi label="Próximas ofertas registradas" valor={marketplacesPeriodo.filter(row => row.proximas_ofertas).length} tono="#4ade80" />
              </div>
              <VisualCard title={`Marketplace y Soluciones (${periodo.label})`} className="xl:col-span-12">
                {qbrTable(marketplacesPeriodo, [
                  { key: 'fabricante', label: 'Fabricante' },
                  { key: 'ofertas_actuales', label: 'Ofertas actuales' },
                  { key: 'estatus', label: 'Estatus' },
                  { key: 'link_publicacion', label: 'Publicación', render: row => row.link_publicacion ? <a href={row.link_publicacion} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">Ver oferta</a> : '—' },
                  { key: 'fecha_publicacion', label: 'Fecha de publicación', render: row => fmtFecha(row.fecha_publicacion) },
                  { key: 'proximas_ofertas', label: 'Próximas ofertas' },
                  { key: 'fecha_publicacion_ofertas_proximas', label: 'Próxima publicación', render: row => fmtFecha(row.fecha_publicacion_ofertas_proximas) },
                ], 'No hay ofertas registradas para el periodo seleccionado.')}
              </VisualCard>
            </div>
          )}
          {tab === 'especializaciones' && (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
              <div className="xl:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Kpi label={`Especializaciones en ${periodo.label}`} valor={especializacionesPeriodo.length} tono={ACCENT} />
                <Kpi label="Requieren renovación" valor={especializacionesPeriodo.filter(row => row.requiere_renovacion).length} tono="#facc15" />
              </div>
              <VisualCard title={`Especializaciones (${periodo.label})`} className="xl:col-span-12">
                {qbrTable(especializacionesPeriodo, [
                  { key: 'fabricante', label: 'Fabricante' },
                  { key: 'tipo', label: 'Tipo' },
                  { key: 'nombre', label: 'Nombre' },
                  { key: 'fecha_obtencion', label: 'Fecha de obtención', render: row => fmtFecha(row.fecha_obtencion) },
                  { key: 'beneficios', label: 'Beneficios' },
                  { key: 'requiere_auditoria', label: 'Auditoría', render: row => row.requiere_auditoria == null ? '—' : (row.requiere_auditoria ? 'Sí' : 'No') },
                  { key: 'fecha_auditoria', label: 'Fecha de auditoría', render: row => fmtFecha(row.fecha_auditoria) },
                  { key: 'requiere_renovacion', label: 'Renovación', render: row => row.requiere_renovacion == null ? '—' : (row.requiere_renovacion ? 'Sí' : 'No') },
                  { key: 'fecha_renovacion', label: 'Fecha de renovación', render: row => fmtFecha(row.fecha_renovacion) },
                  { key: 'proveedor_auditoria', label: 'Proveedor de auditoría' },
                ], 'No hay especializaciones en el periodo seleccionado.')}
              </VisualCard>
            </div>
          )}
        </>
      )}
    </div>
  );
}
